import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { getIn, useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow, BaseSelect } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { ICreateFranchise } from '~/utils/interface/franchise';
import styles from './EditFranchiseDialog.module.scss';
import classNames from 'classnames/bind';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleCreateFranchise, handleUpdateFranchise } from '~/thunks/franchise/franchiseThunk';
import { franchiseActions } from '~/thunks/franchise/franchiseSlice';
import { NumericFormat } from 'react-number-format';
import { handleGetSelectionCompanies } from '~/thunks/company/companyThunk';

const cx = classNames.bind(styles);
type EditFranchiseDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  title?: string;
  data?: ICreateFranchise;
  children?: React.ReactNode;
};

const initialValues: ICreateFranchise = {
  location: {
    name: '',
    code: '',
  },
  dataInitialization: {
    prices: false,
    sourceFranchiseId: undefined,
  },
};

const EditFranchiseDialog = (props: EditFranchiseDialogProps) => {
  const { t } = useTranslation('superadmin/franchise');
  const { mode, data } = props;
  const dispatch = useAppDispatch();
  const { companies } = useAppSelector((state) => state.company);

  useEffect(() => {
    if (companies.length === 0) {
      dispatch(handleGetSelectionCompanies());
    }
  }, [dispatch, companies.length]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (mode === DIALOG_MODE_CREATE) {
      form.resetForm();
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError, setSubmitting }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          setSubmitting(true);
          const payload = { ...values };
          if (!payload.dataInitialization?.prices) {
            delete payload.dataInitialization;
          } else {
            if (!payload.dataInitialization.sourceFranchiseId) {
              setFieldError('dataInitialization.sourceFranchiseId', 'is required');
              setSubmitting(false);
              return;
            }
            payload.dataInitialization.sourceFranchiseId = Number(payload.dataInitialization.sourceFranchiseId);
          }
          await dispatch(handleCreateFranchise(payload)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false);
          dispatch(franchiseActions.changeNeedRefreshData(true));
          dispatch(handleGetSelectionCompanies());
          setSubmitting(false);
        } catch (e: unknown) {
          setSubmitting(false);
          handleApiError(e, setFieldError);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        setSubmitting(true);
        const { location, ...valuesWithoutLocation } = values;
        await dispatch(handleUpdateFranchise({ id: data?.id, data: valuesWithoutLocation })).unwrap();
        handleSetOpen(false);
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        dispatch(franchiseActions.changeNeedRefreshData(true));
        dispatch(handleGetSelectionCompanies());
        setSubmitting(false);
        try {
        } catch (e: unknown) {
          setSubmitting(false);
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('tax', cast(data?.tax));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addfranchise') : t('Editfranchise')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel required={true} className={cx('franchiseName')}>
                {t('name')}
              </InputLabel>
              <TextField
                name='name'
                placeholder={t('name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel className={cx('tax')}>{t('tax')}</InputLabel>
              <NumericFormat
                customInput={TextField}
                name='tax'
                placeholder={t('tax')}
                value={form.values?.tax}
                onValueChange={(values) => {
                  form.setFieldValue('tax', values.floatValue);
                }}
                thousandSeparator={true}
                error={Boolean(form.errors?.tax)}
                helperText={form.errors?.tax}
                fullWidth
                decimalScale={2}
              />
            </Box>
          </BaseRow>
          {mode === DIALOG_MODE_CREATE && (
            <>
              <Box className='text-[#000000] mb-[20px]'>{t('franchiseAdmin')}</Box>

              <BaseRow className='gap-[20px] mb-[10px]'>
                <Box className='w-full'>
                  <InputLabel required={true} className={cx('name')}>
                    {t('name')}
                  </InputLabel>
                  <TextField
                    name='account.name'
                    placeholder={t('nameFranchise')}
                    value={form.values?.account?.name}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'account.name'))}
                    helperText={getIn(form.errors, 'account.name')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel required={true} className={cx('franchiseName')}>
                    {t('email')}
                  </InputLabel>
                  <TextField
                    name='account.email'
                    placeholder={t('email')}
                    value={form.values?.account?.email}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'account.email'))}
                    helperText={getIn(form.errors, 'account.email')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel required={true} className={cx('franchiseName')}>
                    {t('password')}
                  </InputLabel>
                  <TextField
                    name='account.password'
                    type='password'
                    placeholder={t('password')}
                    value={form.values?.account?.password}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'account.password'))}
                    helperText={getIn(form.errors, 'account.password')}
                  />
                </Box>
              </BaseRow>

              <Box className='text-[#000000] mb-[20px]'>{t('location')}</Box>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel required={true} className={cx('franchiseName')}>
                    {t('locationName')}
                  </InputLabel>
                  <TextField
                    name='location.name'
                    placeholder={t('locationName')}
                    value={form.values?.location?.name}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.name'))}
                    helperText={getIn(form.errors, 'location.name')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel required={true} className={cx('franchiseName')}>
                    {t('locationCode')}
                  </InputLabel>
                  <TextField
                    name='location.code'
                    placeholder={t('locationCode')}
                    value={form.values?.location?.code}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.code'))}
                    helperText={getIn(form.errors, 'location.code')}
                  />
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel className={cx('franchiseName')}>{t('address1')}</InputLabel>
                  <TextField
                    name='location.address'
                    placeholder={t('address1')}
                    value={form.values?.location?.address}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.address'))}
                    helperText={getIn(form.errors, 'location.address')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel className={cx('franchiseName')}>{t('address2')}</InputLabel>
                  <TextField
                    name='location.address2'
                    placeholder={t('address2')}
                    value={form.values?.location?.address2}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.address2'))}
                    helperText={getIn(form.errors, 'location.address2')}
                  />
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel className={cx('franchiseName')}>{t('city')}</InputLabel>
                  <TextField
                    name='location.city'
                    placeholder={t('city')}
                    value={form.values?.location?.city}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.city'))}
                    helperText={getIn(form.errors, 'location.city')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel className={cx('franchiseName')}>{t('state')}</InputLabel>
                  <TextField
                    name='location.state'
                    placeholder={t('state')}
                    value={form.values?.location?.state}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.state'))}
                    helperText={getIn(form.errors, 'location.state')}
                  />
                </Box>
              </BaseRow>

              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel className={cx('franchiseName')}>{t('zipCode')}</InputLabel>
                  <TextField
                    name='location.zipCode'
                    placeholder={t('zipCode')}
                    value={form.values?.location?.zipCode}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.zipCode'))}
                    helperText={getIn(form.errors, 'location.zipCode')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel className={cx('franchiseName')}>{t('country')}</InputLabel>
                  <TextField
                    name='location.country'
                    placeholder={t('country')}
                    value={form.values?.location?.country}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, 'location.country'))}
                    helperText={getIn(form.errors, 'location.country')}
                  />
                </Box>
              </BaseRow>

              <Box className='text-[#000000] mb-[20px]'>{t('dataInitialization')}</Box>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.values?.dataInitialization?.prices}
                        onChange={(e) => {
                          form.setFieldValue('dataInitialization.prices', e.target.checked);
                        }}
                      />
                    }
                    label={t('priceLists')}
                  />
                </Box>

                <Box className='w-full'>
                  <InputLabel required={form.values?.dataInitialization?.prices} className={cx('franchiseName')}>
                    {t('franchise')}
                  </InputLabel>
                  <BaseSelect
                    placeholder={t('selectFranchise')}
                    options={companies}
                    value={form.values?.dataInitialization?.sourceFranchiseId}
                    onChange={(e) => {
                      form.setFieldValue('dataInitialization.sourceFranchiseId', e.target.value);
                    }}
                    error={Boolean(getIn(form.errors, 'dataInitialization.sourceFranchiseId'))}
                    helperText={getIn(form.errors, 'dataInitialization.sourceFranchiseId')}
                    disabled={!form.values?.dataInitialization?.prices}
                    isHideIconCheckbox={true}
                  />
                </Box>
              </BaseRow>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={form.isSubmitting}
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditFranchiseDialog);
